import { useTranslation } from 'react-i18next';
import styles from './Match.module.css';
import { formatDateTime, getMatchDateLabel } from 'helpers/dateHelper';
import classNames from 'classnames';
import { useContext, useEffect, useState } from 'react';
import { MatchOffer } from 'types/MatchOffer';
import { getAllBets, getBetByName, getFullTimeOdds } from 'helpers/matchHelper';
import { OtherBets } from 'components/OtherBets/OtherBets';
import { GroupedOtherBets } from 'components/OtherBets/GroupedOtherBets';
import { FULL_TIME_BETGROUP_NAME } from 'config/constants';
import { SlipContext } from 'context/slipContext';
import { getDefaultSportIconSrc } from 'helpers/sportIcon';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { MatchInsightsModal } from 'components/MatchInsights/MatchInsightsModal';

interface Props {
  match: MatchOffer;
  sportId: number;
  betName?: string;
  noBg?: boolean;
}

export const Match = ({ match, sportId, betName, noBg = false }: Props) => {
  const { t } = useTranslation();
  const [homeWinSelected, setHomeWinSelected] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [drawSelected, setDrawSelected] = useState(false);
  const [awayWinSelected, setAwayWinSelected] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const { entries, addSlipEntry, removeSlipEntry, entryExist } =
    useContext(SlipContext);

  const dateTime = new Date(match.time * 1000);
  const { homeWin, draw, awayWin } = getFullTimeOdds(match);
  const homeName = match.home?.name ?? match.league?.name ?? 'Unknown';
  const awayName = match.away?.name ?? '-';

  useEffect(() => {
    if (homeWin) {
      if (homeWinSelected && !entryExist(match, match.fullTimeBet, homeWin)) {
        setHomeWinSelected(false);
      } else if (
        !homeWinSelected &&
        entryExist(match, match.fullTimeBet, homeWin)
      )
        setHomeWinSelected(true);
    }

    if (draw) {
      if (drawSelected && !entryExist(match, match.fullTimeBet, draw)) {
        setDrawSelected(false);
      } else if (!homeWinSelected && entryExist(match, match.fullTimeBet, draw))
        setDrawSelected(true);
    }

    if (awayWin) {
      if (awayWinSelected && !entryExist(match, match.fullTimeBet, awayWin)) {
        setAwayWinSelected(false);
      } else if (
        !homeWinSelected &&
        entryExist(match, match.fullTimeBet, awayWin)
      )
        setAwayWinSelected(true);
    }
  }, [entries]);

  const expandClickHandler = () => {
    setExpanded(!expanded);
    setScrollY(window.scrollY);
  };

  const closeClickHandler = () => {
    setExpanded(false);
    window.scrollTo({ top: scrollY });
  };

  let selectedBet = null;
  if (betName) {
    selectedBet = getBetByName(match, betName);
  }

  const handle1x2Click = (bet: '1' | 'Draw' | '2') => {
    const odd = match.fullTimeBet.odds?.filter((x) => x.name === bet)[0];

    if (
      odd &&
      ((homeWinSelected && bet === '1') ||
        (drawSelected && bet === 'Draw') ||
        (awayWinSelected && bet === '2'))
    ) {
      removeSlipEntry(match, match.fullTimeBet, odd);
    } else {
      addSlipEntry(match, match.fullTimeBet, odd);
    }
  };

  const getIconsJSX = () => {
    return (
      <>
        <ThemeIcon
          icon="stats-on"
          alt={t('match_statistics_label')}
          title={t('match_statistics_label')}
          variant="inline"
          className={classNames(styles.icon, styles.iconButton)}
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            setShowInsights(true);
          }}
        />
      </>
    );
  };

  return (
    <>
      {showInsights && (
        <MatchInsightsModal
          fi={match.fi}
          onClose={() => setShowInsights(false)}
        />
      )}
      <div
        className={classNames(styles.wrapper, {
          [styles['noBg']]: noBg,
          [styles['noBottomBorder']]: betName != null,
        })}
      >
        {getAllBets(match).length > 0 && (
          <div className={styles.betGroupNumber} onClick={expandClickHandler}>
            +{getAllBets(match).length}
            <ThemeIcon icon="chevron-right-blue" alt="More" variant="inline" />
          </div>
        )}
        <div className={styles.dateTime}>
          <span>{t(getMatchDateLabel(dateTime))} </span>
          <span>
            {formatDateTime(dateTime, {
              removeDate: true,
              removeSeconds: true,
            })}
          </span>
        </div>
        <div className={styles.teams}>
          <span>{homeName}</span>
          <SportIcon
            sportId={sportId ?? 1}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = getDefaultSportIconSrc();
            }}
            alt={sportId + ''}
          />
          <span>{awayName}</span>
        </div>
        {(!betName || betName === FULL_TIME_BETGROUP_NAME) && (
          <>
            <div className={styles.bets1x2}>
              {homeWin?.odds && (
                <div
                  className={classNames(styles.bet, {
                    [styles['betSelected']]: homeWinSelected,
                  })}
                  onClick={() => {
                    setHomeWinSelected(!homeWinSelected);
                    handle1x2Click('1');
                  }}
                >
                  {homeWin.odds.toFixed(2)}
                </div>
              )}
              {draw?.odds ? (
                <div
                  className={classNames(styles.bet, {
                    [styles['betSelected']]: drawSelected,
                  })}
                  onClick={() => {
                    setDrawSelected(!drawSelected);
                    handle1x2Click('Draw');
                  }}
                >
                  {draw.odds.toFixed(2)}
                </div>
              ) : (
                // Two-way markets keep the X column empty so "2" stays under 2.
                (homeWin?.odds || awayWin?.odds) && (
                  <div
                    className={classNames(styles.bet, styles.betEmpty)}
                    aria-hidden="true"
                  />
                )
              )}
              {awayWin?.odds && (
                <div
                  className={classNames(styles.bet, {
                    [styles['betSelected']]: awayWinSelected,
                  })}
                  onClick={() => {
                    setAwayWinSelected(!awayWinSelected);
                    handle1x2Click('2');
                  }}
                >
                  {awayWin.odds.toFixed(2)}
                </div>
              )}
            </div>

            <div className={styles.iconsWrapper}>{getIconsJSX()}</div>
          </>
        )}
      </div>
      {betName &&
        betName !== FULL_TIME_BETGROUP_NAME &&
        (selectedBet ? (
          <OtherBets
            noBg={noBg}
            noTopBorder
            bets={[selectedBet]}
            match={match}
          />
        ) : (
          <div className={styles.noOfferMessage}>
            {t('no_bet_offer_message')}
          </div>
        ))}

      {expanded && (
        <div className={styles.betGroupsWrapper}>
          <GroupedOtherBets bets={getAllBets(match)} match={match} />
          <div className={styles.close}>
            <a onClick={closeClickHandler}>Close</a>
          </div>
        </div>
      )}
    </>
  );
};
